import React from 'react';
import { Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import StatusIcon, { STEP_STATUS } from './StatusIcon';
import WarningBar from './WarningBar';

/**
 * SiteGroupHeader: heading of a group of stops at one site on the run list.
 * Props:
 * - name, address
 * - doneCount, totalCount: shown as "3/5" (green when all done)
 * - warnings: site-level requirements (strings), shown under the header
 * - importantNotes: important site notes, shown in red under the warnings
 * - isCollapsed: when true, renders one summary row (collapsedLabel, e.g.
 *   "Both machines done") instead of the stops; the parent hides the stops
 * - onToggle: makes the header and the summary row expand/collapse the group
 * - collapsedLabel (default "All machines done")
 * - style
 */
const SiteGroupHeader = ({
	name,
	address,
	doneCount = 0,
	totalCount = 0,
	warnings,
	importantNotes,
	isCollapsed = false,
	onToggle,
	collapsedLabel = 'All machines done',
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const isComplete = totalCount > 0 && doneCount >= totalCount;
	const countLabel = `${doneCount}/${totalCount}`;

	const header = (
		<>
			<View style={styles.titles}>
				<Text style={styles.name} accessibilityRole="header">{name}</Text>
				{Boolean(address) && <Text style={styles.address}>{address}</Text>}
			</View>
			<Text style={[styles.count, isComplete && styles.countComplete]}>{countLabel}</Text>
			{Boolean(onToggle) && !isCollapsed && (
				<Feather name="chevron-up" size={20} color={colors.iconMuted} />
			)}
		</>
	);

	return (
		<View style={[styles.container, style]}>
			{onToggle && !isCollapsed ? (
				<Pressable
					onPress={onToggle}
					accessibilityRole="button"
					accessibilityLabel={`${name}, ${countLabel} done`}
					accessibilityHint="Collapses this site"
					accessibilityState={{ expanded: true }}
					style={[styles.header, styles.headerPressable]}
				>
					{header}
				</Pressable>
			) : (
				<View style={styles.header} accessible={true} accessibilityLabel={`${name}, ${countLabel} done`}>
					{header}
				</View>
			)}
			<WarningBar items={warnings} size="compact" />
			<WarningBar items={importantNotes} size="compact" tone="danger" />
			{isCollapsed && (
				<Pressable
					onPress={onToggle}
					disabled={!onToggle}
					accessibilityRole="button"
					accessibilityLabel={collapsedLabel}
					accessibilityHint="Shows the stops at this site"
					accessibilityState={{ expanded: false }}
					style={({ pressed }) => [styles.summary, pressed && styles.pressed]}
				>
					<StatusIcon status={STEP_STATUS.DONE} size={22} />
					<Text style={styles.summaryText}>{collapsedLabel}</Text>
					{Boolean(onToggle) && <Feather name="chevron-down" size={20} color={colors.iconMuted} />}
				</Pressable>
			)}
		</View>
	);
};

const createStyles = ({ ui: { colors, radius, type, space } }) => ({
	container: {
		gap: space.sm,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'flex-end',
		gap: space.sm,
	},
	// Only the tappable (expanded done site) header needs the 44 touch height.
	headerPressable: {
		minHeight: 44,
	},
	titles: {
		flex: 1,
		gap: 2,
	},
	name: {
		...type.sectionTitle,
		color: colors.text,
	},
	address: {
		...type.caption,
		color: colors.textSecondary,
	},
	count: {
		...type.secondarySemibold,
		color: colors.textSecondary,
	},
	countComplete: {
		color: colors.success,
	},
	summary: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		minHeight: 48,
		paddingHorizontal: 14,
		borderRadius: radius.card,
		backgroundColor: colors.card,
	},
	summaryText: {
		...type.bodySmall,
		color: colors.textSecondary,
		flex: 1,
	},
	pressed: {
		opacity: 0.85,
	},
});

export default SiteGroupHeader;
